(ns ui.dag
  "Generic React Flow (xyflow) wrapper.

   Composable design — `dag` is the canvas; everything else (background,
   zoom buttons, viewport controller, overlay slots) is a separate
   component you pass as a child. Children render inside the canvas and
   can freely use `useReactFlow` hooks.

   Must be rendered inside a `[:> ReactFlowProvider ...]` (xyflow requirement).

   Example:
     [:> ReactFlowProvider
      [ui.dag/dag {:nodes nodes :edges edges :node-types my-types :dark-mode? dark?}
       [ui.dag/background {:dark? dark?}]
       [ui.dag/zoom-controls {:focus-node-id opened-id}]
       [ui.dag/viewport-controller {:focus-node-id opened-id :fit-token token}]
       [ui.dag/overlay {:position :top-left}  [filter-tabs]]
       [ui.dag/overlay {:position :top-right} [actions]]]]

   Node primitives (Radix-style composition):
     [ui.dag/node-group                       ;; outer hover wrapper
      [ui.dag/node-root {:opened? ... }        ;; clickable, holds state classes
       [ui.dag/node-status-bar {:color ...}]   ;; optional left-edge bar
       [ui.dag/node-content                    ;; flex row layout
        [ui.dag/node-icon [Icon ...]]
        [ui.dag/node-text                      ;; text column (overflow-hidden)
         [ui.dag/node-title    \"title\"]
         [ui.dag/node-subtitle \"subtitle\"]]]
       [my-footer]]                            ;; any extra children
      [my-badge]]                              ;; absolute siblings of node-root"
  (:require
   ["@xyflow/react" :refer [applyEdgeChanges Background BackgroundVariant
                            Handle MiniMap Position
                            ReactFlow useReactFlow useStore]]
   ["lucide-react" :refer [Maximize Minus Plus]]
   ["react" :as react]
   [applied-science.js-interop :as jsi]
   [ui.button :as button]
   [ui.utils :refer [clsx separate-props]]))


;; =============================================================================
;; Hooks & primitives
;; =============================================================================

(defn use-center
  "React hook returning a fn `(node-id) -> centers viewport on that node`.

   Resolves the node's *absolute* position via `getInternalNode`
   (`internals.positionAbsolute`) rather than its raw `:position`: nodes nested
   in a parent container carry a parent-relative `:position`, so centering on
   that would land the viewport offset by the container's origin. Falls back to
   the relative position when no internal node is available. Resolution happens
   at call time, so it always reads the latest layout."
  []
  (let [{:keys [setCenter getInternalNode]} (js->clj (useReactFlow) :keywordize-keys true)]
    (fn [node-id]
      (when-let [node (and node-id setCenter getInternalNode (getInternalNode node-id))]
        (let [x      (or (jsi/get-in node [:internals :positionAbsolute :x])
                         (jsi/get-in node [:position :x]) 0)
              y      (or (jsi/get-in node [:internals :positionAbsolute :y])
                         (jsi/get-in node [:position :y]) 0)
              width  (or (jsi/get-in node [:measured :width])  0)
              height (or (jsi/get-in node [:measured :height]) 0)]
          (setCenter (+ x (/ width 2))
                     (+ y (/ height 2))
                     #js {:zoom 1 :duration 100})
          ;; truthy = centred, so a caller can fall back when the node is gone
          true)))))


(defn use-zoom
  "React hook returning the current viewport zoom level (xyflow `transform[2]`).
   Re-renders the calling component as the user zooms. Must be called inside a
   function component (`:f>`) rendered within the React Flow canvas."
  []
  (useStore (fn [^js s] (aget (jsi/get s :transform) 2))))


(defonce collapsed-context
  ;; Canvas-level \"is the viewport zoomed below the collapse threshold\" flag,
  ;; computed once by `dag` (see its `:collapse-below-zoom` prop) and read by node
  ;; components via `use-collapsed`. Replaces a per-node `use-zoom`/`useStore`
  ;; subscription: the boolean only flips at the threshold, so a graph with many
  ;; nodes re-renders them on the crossing rather than on every zoom tick.
  (react/createContext false))

(defn use-collapsed
  "Hook: true when the canvas is zoomed below the `:collapse-below-zoom` threshold
   passed to `dag`. A cheap React context read (no per-node store subscription) —
   the value changes only when the threshold is crossed. Call inside a node
   component rendered within the canvas."
  []
  (react/useContext collapsed-context))


(def highlight-edge-class
  "CSS class for a highlighted edge (see tailwind.css)."
  "dag-edge-highlight")


;; Unselectable edges get `pointer-events: none`, so a drag starting on one pans;
;; React Flow marks every edge `nopan` otherwise.
(def default-edge-options
  #js {:style      #js {:strokeWidth 2}
       :selectable false})


(defn- with-class
  "Stamps `class` on the items whose id passes `on?`, replacing only the items
   whose state changes — React Flow skips the rest."
  [items class on?]
  (.map items
        (fn [^js item]
          (let [want? (boolean (on? (jsi/get item :id)))
                has?  (= (jsi/get item :className) class)]
            (if (= want? has?)
              item
              (js/Object.assign #js {} item
                                #js {:className (when want? class)}))))))


(defn- lineage-index [edges]
  {:out (group-by :source edges) :in (group-by :target edges)})

(defn- lineage-edge-ids
  "Ids of every edge upstream and downstream of `node-id`."
  [{:keys [out in]} node-id]
  (let [ids (js/Set.)]
    (doseq [[index far-end] [[out :target] [in :source]]]
      (loop [queue #queue [node-id] seen #{node-id}]
        (when-let [n (peek queue)]
          (let [es    (get index n)
                fresh (into [] (comp (map far-end) (remove seen) (distinct)) es)]
            (doseq [e es] (.add ids (:id e)))
            (recur (into (pop queue) fresh) (into seen fresh))))))
    ids))

(def ^:private dimmed-node-class "dag-node-dimmed")

(defn- lineage-node-ids
  "`node-id`, the ends of `edge-ids`, and their containers (a dimmed container
   fades the cards in it)."
  [nodes edges edge-ids node-id]
  (let [parent (into {} (keep (fn [{:keys [id parentId]}] (when parentId [id parentId]))) nodes)
        ids    (js/Set. #js [node-id])]
    (doseq [{:keys [id source target]} edges
            :when (.has edge-ids id)]
      (.add ids source)
      (.add ids target))
    (doseq [id (vec (es6-iterator-seq (.values ids)))]
      (loop [p (parent id)]
        (when (and p (not (.has ids p)))
          (.add ids p)
          (recur (parent p)))))
    ids))

(defn- set-highlighted-edges!
  "Applies `ids` as the highlighted set in one batched `setEdges`, skipping no-ops."
  [^js flow ids-ref ids]
  (let [prev (jsi/get ids-ref :current)]
    (when-not (and (= (.-size prev) (.-size ids))
                   (every? #(.has prev %) (es6-iterator-seq (.values ids))))
      (jsi/assoc! ids-ref :current ids)
      (jsi/call flow :setEdges #(with-class % highlight-edge-class (fn [id] (.has ids id)))))))


;; =============================================================================
;; Composable children
;; =============================================================================

(defn background
  "Kleene-styled dotted background. Pass `:dark?` to invert colors.
   Any xyflow Background props can be merged via the props map."
  ([] [background nil])
  ([{:keys [dark?] :as props}]
   [:> Background
    (merge {:variant  (jsi/get BackgroundVariant :Dots)
            :bg-color (if dark? "var(--color-slate-900)" "var(--color-kleene-background-secondary)")
            :color    (if dark? "var(--color-slate-800)" "var(--color-slate-200)")
            :gap      32
            :size     4}
           (dissoc props :dark?))]))


(defn minimap
  "Kleene-styled xyflow MiniMap. Pannable + zoomable by default.
   Any xyflow MiniMap props can be merged via the props map; pass `:node-color`
   (fn of the js node -> color string) to color nodes by type/status."
  ([] [minimap nil])
  ([{:keys [class] :as props}]
   [:> MiniMap
    (merge {:pannable true
            :zoomable true
            :position "bottom-left"
            :bg-color "var(--color-kleene-background)"}
           (dissoc props :dark? :class)
           {:class (clsx "relative! m-0! rounded! border! shadow-md!" class)})]))


(defn- zoom-controls-inner [{:keys [focus-node-id]}]
  (let [{:keys [zoomIn zoomOut fitView]} (js->clj (useReactFlow) :keywordize-keys true)
        set-center (use-center)]
    ;; Shares the canvas toolbars' `p-0.5` pill wrapper, but keeps 16px glyphs (the
    ;; `:size :icon` default) rather than their 24px: these are secondary viewport
    ;; nudges, and at 24 the stack of three dominated the corner.
    [:div {:class "flex flex-col items-center gap-px p-0.5 bg-kleene-background rounded border shadow-md"}
     [button/button {:icon     Minus
                     :size     :icon
                     :variant  :empty-transparent
                     :title    "Zoom out"
                     :on-click zoomOut}]
     [button/button {:icon     Plus
                     :size     :icon
                     :variant  :empty-transparent
                     :title    "Zoom in"
                     :on-click zoomIn}]
     ;; Always shown: centers on the focused node when there is one, otherwise fits
     ;; the whole graph in view. (Previously the whole button was hidden without a
     ;; focus node, so the fit-view fallback never rendered.)
     [button/button {:icon     Maximize
                     :size     :icon
                     :variant  :empty-transparent
                     :title    (if focus-node-id "Center on selected node" "Fit view")
                     :on-click (if focus-node-id
                                 #(set-center focus-node-id)
                                 #(when fitView (fitView)))}]]))


(defn zoom-controls
  "Vertical zoom in/out + optional 'focus' button (shown when `:focus-node-id`
   is given and resolves to a node).

   Position-agnostic — wrap in [overlay] for placement:
     [ui.dag/overlay {:position :bottom-left}
      [ui.dag/zoom-controls {:focus-node-id id}]]"
  [props]
  [:f> zoom-controls-inner (or props {})])


(defn- viewport-controller-inner
  [{:keys [focus-node-id focus-token fit-token delay-ms] :or {delay-ms 350}}]
  (let [{:keys [fitView]} (js->clj (useReactFlow) :keywordize-keys true)
        set-center        (use-center)
        mounted?          (react/useRef false)
        ;; Which dep MOVED, not which is set: `:focus-node-id` is sticky in app-db
        ;; (nothing clears it), so branching on its truthiness would centre forever
        ;; and never refit again once the user had focused anything.
        last-focus        (react/useRef nil)
        last-fit          (react/useRef fit-token)]
    (react/useEffect
     (fn []
       (let [focus    [focus-node-id focus-token]
             refocus? (and focus-node-id (not= focus (jsi/get last-focus :current)))
             ;; only a new node set refits, not a cleared focus
             refit?   (not= fit-token (jsi/get last-fit :current))
             fit      #(when fitView (fitView #js {:duration 400}))
             act      (cond
                        ;; somewhere to look beats showing everything; if that node
                        ;; is gone (filtered out) fall back to fitting
                        refocus?                    #(when-not (set-center focus-node-id) (fit))
                        ;; the `:fit-view` prop already fits on mount
                        (and refit? (jsi/get mounted? :current)) fit)
             timer    (when act (js/setTimeout act delay-ms))]
         (jsi/assoc! last-focus :current focus)
         (jsi/assoc! last-fit :current fit-token)
         (jsi/assoc! mounted? :current true)
         ;; cancel a move the next change has already superseded
         (fn [] (when timer (js/clearTimeout timer)))))
     #js [focus-node-id focus-token fit-token])
    nil))


(defn viewport-controller
  "Owns the viewport moves the user didn't make: centre on a node, or refit when the
   node set changes. Renders nothing.

   One effect and one timer, because as two components they raced — the refit landed
   50ms after the centring and undid it whenever a node-set change and a focus came
   out of the same update, which is exactly what arriving at `/dag?focus=<id>` does.

   Centring wins only on the update where the focus itself CHANGED — callers hold
   `:focus-node-id` indefinitely, so a set focus can't be read as \"centre now\".

   Props:
     :focus-node-id  node to centre on when it (or `:focus-token`) changes
     :focus-token    changes to re-centre on the SAME node
     :fit-token      derived from the node set — a change refits, so the graph
                     re-frames after a filter drops nodes, but not on same-node
                     updates like a status refresh
     :delay-ms       lets new nodes apply and measure first (default 350)"
  [props]
  [:f> viewport-controller-inner (or props {})])


(defn- hover-tooltip-inner [{:keys [class render-node]}]
  (let [^js dom (useStore (fn [^js s] (jsi/get s :domNode)))
        ^js flow (useReactFlow)
        [tip set-tip] (react/useState nil)
        shown (react/useRef nil)]
    (react/useEffect
     (fn []
       (if-not dom
         js/undefined
         (let [show (fn [^js e]
                      (let [el (some-> (jsi/get e :target)
                                       (jsi/call :closest "[data-tooltip]"))]
                        (when-not (identical? el (jsi/get shown :current))
                          (jsi/assoc! shown :current el)
                          (set-tip
                           (when el
                             (let [r (jsi/call el :getBoundingClientRect)]
                               #js {:title (jsi/call el :getAttribute "data-tooltip")
                                    :desc  (jsi/call el :getAttribute "data-tooltip-desc")
                                    :node  (some->> (some-> (jsi/call el :closest ".react-flow__node")
                                                            (jsi/call :getAttribute "data-id"))
                                                    (jsi/call flow :getNode))
                                    :x     (+ (jsi/get r :left) (/ (jsi/get r :width) 2))
                                    :y     (jsi/get r :top)}))))))
               hide (fn []
                      (jsi/assoc! shown :current nil)
                      (set-tip nil))]
           ;; pointer-down and wheel dismiss it, so it never lingers over a moved graph
           (jsi/call dom :addEventListener "pointerover" show)
           (jsi/call dom :addEventListener "pointerleave" hide)
           (jsi/call dom :addEventListener "pointerdown" hide)
           (jsi/call dom :addEventListener "wheel" hide #js {:passive true})
           (fn []
             (jsi/call dom :removeEventListener "pointerover" show)
             (jsi/call dom :removeEventListener "pointerleave" hide)
             (jsi/call dom :removeEventListener "pointerdown" hide)
             (jsi/call dom :removeEventListener "wheel" hide)))))
     #js [dom])
    (if-let [body (when-let [node (and tip render-node (jsi/get tip :node))]
                    (render-node node))]
      (let [^js node (jsi/get tip :node)]
        [:div {:class "fixed z-9999 pointer-events-none -translate-x-1/2 -translate-y-full shadow-lg rounded-md"
               :style {:left  (jsi/get tip :x)
                       :top   (- (jsi/get tip :y) 8)
                       :width (or (jsi/get-in node [:measured :width]) (jsi/get node :width))}}
         body])
      (when tip
        [:div {:class (clsx "fixed z-9999 pointer-events-none -translate-x-1/2 -translate-y-full"
                            "px-2 py-1 rounded-md border shadow-md text-sm"
                            "bg-white dark:bg-kleene-dark-02 border-kleene-neutral-05 dark:border-kleene-dark-03"
                            class)
               :style {:left (jsi/get tip :x) :top (- (jsi/get tip :y) 8)}}
         [:p {:class "font-medium whitespace-nowrap"} (jsi/get tip :title)]
         (when-let [desc (jsi/get tip :desc)]
           [:p {:class "text-kleene-text-secondary whitespace-nowrap"} desc])]))))


(defn hover-tooltip
  "One tooltip for the canvas, reading `data-tooltip`/`data-tooltip-desc` off the hovered
   element; render it as a child of `dag` so it sits outside the viewport transform.
   `:render-node`, given the hovered xyflow node, may return hiccup to show instead."
  ([] [hover-tooltip nil])
  ([props] [:f> hover-tooltip-inner (or props {})]))


(defn overlay
  "Absolutely-positioned overlay rendered on top of the canvas.
   Consumes `:position` — :top-left (default) | :top-right | :bottom-left | :bottom-right.
   Extra props pass through to the wrapper."
  [& args]
  (let [[props children] (separate-props args)
        position         (or (:position props) :top-left)
        pos-class        (case position
                           :top-left     "top-2 left-2"
                           :top-right    "top-2 right-2"
                           :bottom-left  "bottom-2 left-2"
                           :bottom-right "bottom-2 right-2"
                           "top-2 left-2")]
    (into [:div (-> props
                    (dissoc :position)
                    (update :class #(clsx "absolute z-10" pos-class %)))]
          children)))


;; =============================================================================
;; Canvas
;; =============================================================================

(defn- dag-impl
  [{:keys [nodes edges node-types edge-types
           dark-mode? fit-view? min-zoom max-zoom
           hover-highlight-edges? hover-lineage? pan-activation-key-code read-only?
           collapse-below-zoom only-render-visible?
           pinned-node-id on-unpin
           on-node-click on-node-double-click on-node-context-menu
           children]
    :or   {fit-view?              true
           min-zoom               0.2
           max-zoom               1
           hover-highlight-edges? true
           only-render-visible?   false}}]
  (let [[js-nodes set-nodes] (react/useState (clj->js (or nodes [])))
        [js-edges set-edges] (react/useState (clj->js (or edges [])))
        ^js flow           (useReactFlow)
        highlighted        (react/useRef (js/Set.))
        ;; Only a node on this canvas pins: the id outlives tab switches and filters,
        ;; and a missing one would fade the whole graph.
        pinned?            (react/useMemo #(boolean (and pinned-node-id
                                                         (some (fn [n] (= pinned-node-id (:id n))) nodes)))
                                          #js [pinned-node-id nodes])
        lineage           (react/useMemo #(when (or hover-lineage? pinned?) (lineage-index edges))
                                          #js [edges hover-lineage? pinned?])
        ;; One canvas-level zoom read replaces a `useStore` in every node: this
        ;; boolean only flips when the viewport crosses `collapse-below-zoom`, so
        ;; nodes re-render on the crossing, not on every zoom tick (see
        ;; `collapsed-context` / `use-collapsed`). A nil threshold stays constant
        ;; false, so the selector never triggers a re-render for callers not using it.
        collapsed? (useStore (fn [^js s]
                               (boolean (when collapse-below-zoom
                                          (< (aget (jsi/get s :transform) 2) collapse-below-zoom)))))]
    (react/useEffect
     (fn []
       (set-nodes (clj->js (or nodes [])))
       (set-edges (clj->js (or edges [])))
       js/undefined)
     #js [nodes edges])

    ;; after the reset above, so a fresh node list is re-dimmed
    (react/useEffect
     (fn []
       (if pinned?
         (let [edge-ids (lineage-edge-ids lineage pinned-node-id)
               lit      (lineage-node-ids nodes edges edge-ids pinned-node-id)]
           (set-nodes #(with-class % dimmed-node-class (fn [id] (not (.has lit id)))))
           (set-highlighted-edges! flow highlighted edge-ids))
         (do (set-nodes #(with-class % dimmed-node-class (constantly false)))
             (set-highlighted-edges! flow highlighted (js/Set.))))
       js/undefined)
     #js [pinned? pinned-node-id nodes edges lineage])

    (react/useEffect
     (fn []
       (if (and pinned? on-unpin)
         ;; skips an Esc a popover or other gesture already handled
         (let [f #(when (and (= "Escape" (jsi/get % :key)) (not (jsi/get % :defaultPrevented)))
                    (on-unpin))]
           (.addEventListener js/document "keydown" f)
           #(.removeEventListener js/document "keydown" f))
         js/undefined))
     #js [pinned? on-unpin])

    [:> (jsi/get collapsed-context :Provider) {:value collapsed?}
     (into
      [:> ReactFlow
       (cond-> {:nodes                        js-nodes
                :edges                        js-edges
                :default-edge-options         default-edge-options
                :fit-view                     fit-view?
                :color-mode                   (if dark-mode? "dark" "light")
                :min-zoom                     min-zoom
                :max-zoom                     max-zoom
                :only-render-visible-elements only-render-visible?
                :pan-activation-key-code      pan-activation-key-code
                :pro-options                  {:hide-attribution true}
                :onEdgesChange                (fn [edge-changes]
                                                (set-edges (applyEdgeChanges edge-changes js-edges)))}
        edge-types
        (assoc :edge-types edge-types)
        read-only?
        (assoc :nodes-draggable      false
               :nodes-connectable    false
               :elements-selectable  false
               :zoom-on-scroll       false
               :zoom-on-pinch        false
               :zoom-on-double-click false
               :pan-on-drag          false
               :pan-on-scroll        false)

        node-types
        (assoc :node-types node-types)

        on-node-click
        (assoc :on-node-click on-node-click)

        on-node-double-click
        (assoc :on-node-double-click on-node-double-click)

        on-node-context-menu
        (assoc :on-node-context-menu on-node-context-menu)

        on-unpin
        (assoc :on-pane-click (fn [_] (when pinned? (on-unpin))))

        pinned?
        (assoc :class-name "dag-lineage-pinned")

        (and hover-highlight-edges? (not pinned?))
        (assoc :on-node-mouse-enter
               (fn [_ ^js node]
                 (set-highlighted-edges!
                  flow highlighted
                  (if lineage
                    (lineage-edge-ids lineage (jsi/get node :id))
                    (let [ids (js/Set.)]
                      (doseq [^js c (jsi/call flow :getNodeConnections
                                              #js {:nodeId (jsi/get node :id)})]
                        (.add ids (jsi/get c :edgeId)))
                      ids))))

               :on-node-mouse-leave
               (fn [_ _] (set-highlighted-edges! flow highlighted (js/Set.)))))]
      children)]))


(defn dag
  "Generic React Flow canvas. Pass children for background, controls, overlays —
   they render inside the canvas and can use `useReactFlow` hooks freely.

   Required props: `:nodes`, `:edges`.
   Optional props: `:node-types`, `:edge-types`, `:dark-mode?`, `:fit-view?`,
   `:min-zoom`, `:max-zoom`, `:hover-highlight-edges?`, `:pan-activation-key-code`,
   `:on-node-click`, `:on-node-double-click`, `:on-node-context-menu` (xyflow
   `(event, node)` — e.g. to open one shared context menu at the cursor).
   `:pinned-node-id` — keeps that node's lineage lit and fades the rest.
   `:on-unpin` — called on an empty-canvas click or Esc.
   `:read-only?` — disables node dragging/selection and all zoom/pan interaction
   (the canvas just displays the graph). Node-internal inputs still work.
   `:only-render-visible?` — cull off-screen nodes from the DOM (xyflow
   `onlyRenderVisibleElements`); needs nodes to carry dimensions. Recommended for
   large graphs (100s–1000s of nodes).
   `:collapse-below-zoom` — zoom level under which `use-collapsed` returns true, so
   node components can render a compact form when zoomed out. Read once at the
   canvas (not per node) and shared via `collapsed-context`.

   See the namespace docstring for a usage example."
  [props & children]
  [:f> dag-impl (assoc props :children children)])


;; =============================================================================
;; Node primitives
;; =============================================================================

(defn- merge-class
  "Merge extra classes onto a props map without losing other attrs."
  [props & extra-classes]
  (update props :class #(apply clsx (concat extra-classes [%]))))


(defn node-group
  "Outer hover-group wrapper for a DAG node. Holds a `node-root` plus any
   absolute-positioned siblings (badges, popovers). All extra props pass
   through to the wrapper element."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:div (merge-class props "group relative hover:z-20")]
          children)))


(defn- root-classes
  "Full class string for `node-root` — combines shape + all state-dependent classes."
  [{:keys [shape opened? selected? highlighted? disabled? loading? state class]
    :or   {shape :rect}}]
  (clsx "block p-3 border overflow-hidden ring-transparent font-medium transition-shadow"
        (case shape
          :pill "rounded-full"
          "relative rounded-md")
        ;; One background class, chosen — not several layered for the stylesheet
        ;; to arbitrate. A highlight tracks the pointer, so it outranks the rest;
        ;; below it the order is what the generated stylesheet already resolved
        ;; these to when they were stacked, so nothing else changes appearance.
        (cond
          highlighted?       "bg-sky-100 dark:bg-sky-900"
          (= state :updated) "bg-kleene-warning-background"
          (= state :created) "bg-kleene-success-background"
          opened?            "bg-kleene-hover-background"
          :else              "bg-kleene-background")
        ;; Same for the ring: highlighted? outranks selected?, and gets a wider
        ;; ring because a 2px one reads as noise against the node's own border.
        (if highlighted? "ring-4" "ring-2")
        {"group-hover:ring-kleene-input-border"                   (not (or selected? highlighted?))
         "ring-kleene-secondary-01 dark:ring-kleene-secondary-v2" (and selected? (not highlighted?))
         "ring-sky-500 shadow-lg"                                 highlighted?
         "pointer-events-none opacity-50"                         disabled?
         "opacity-70"                                             loading?}
        class))


(defn node-root
  "Clickable inner element of a DAG node — holds visual state classes.
   All other props (`:on-click`, `:href`, `:title`, `:data-cy`, etc.) pass through
   to the underlying element. Renders <a> when `:href` is given, otherwise <div>.

   Visual props (consumed; all optional):
     :shape      — :rect (default) or :pill (fully rounded; table style)
     :opened?    — node is the currently-focused tab
     :selected?  — node is selected
     :highlighted? — transiently pointed at from elsewhere (e.g. the execution
                     log); outranks :selected?, matching the gantt's hover colour
     :disabled?  — pointer-events disabled + dim
     :loading?   — slightly translucent
     :state      — :created or :updated (changes bg color)
     :class      — extra classes"
  [& args]
  (let [[props children] (separate-props args)
        tag              (if (:href props) :a :div)
        passthrough      (dissoc props :shape :opened? :selected? :highlighted? :disabled? :loading? :state :class)]
    (into [tag (assoc passthrough :class (root-classes props))]
          children)))


(defn status->color
  "Maps an execution-status string to the Tailwind bg-* class used by
   `node-status-bar` and zoom-collapsed node bodies. Unknown/nil → \"bg-gray-300\"."
  [status]
  (get {"completed" "bg-green-400"
        "failed"    "bg-red-400"
        "pending"   "bg-yellow-400"
        "blocked"   "bg-gray-400"}
       status
       "bg-gray-300"))

(defn node-status-bar
  "Left-edge color bar inside a `node-root`. Use as the first child.
   Consumes `:color` (Tailwind bg-* class — no-op when nil). Extra props pass through."
  [& args]
  (let [[props _] (separate-props args)
        color     (:color props)]
    (when color
      [:div (-> props
                (dissoc :color)
                (merge-class "absolute top-0 left-0 bottom-0 w-1.5" color))])))


(defn node-content
  "Row layout: flex with gap + items-center. Holds icon + text primitives.
   Extra props pass through."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:div (merge-class props "flex gap-2 items-center")] children)))


(defn node-icon
  "Wrapper for the node icon — keeps the icon as its own flex item.
   Extra props pass through."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:div props] children)))


(defn node-text
  "Text column wrapper — `overflow-hidden` + `min-w-0` so title/subtitle ellipsis
   works (min-w-0 lets the flex child shrink below its content width).
   Extra props pass through."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:div (merge-class props "overflow-hidden min-w-0")] children)))


(defn node-title
  "Node title <p>. Defaults the HTML `:title` attr to the rendered text.
   Extra props pass through."
  [& args]
  (let [[props children] (separate-props args)
        text             (first children)]
    [:p (-> props
            (update :title #(or % text))
            (merge-class "text-sm font-medium leading-4 whitespace-nowrap text-ellipsis overflow-hidden"))
     text]))


(defn node-subtitle
  "Node subtitle <p>. Extra props pass through."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:p (merge-class props "text-xs leading-4 text-kleene-text-secondary font-[IBM_Plex_Sans_Condensed]")]
          children)))

(defn node-footer [& args]
  (let [[props children] (separate-props args)]
    (into [:div (merge-class props "-mb-2 mt-1 pt-1 -mx-3 px-3 border-t")]
          children)))

(defn node-handles
  "Wraps node content between hidden xyflow connection handles — a `target`
   handle on the left edge and a `source` handle on the right. Use as the
   body of an xyflow node-type component (the value mapped in `:node-types`).

   Must be rendered by React Flow.

   Example:
     (defn my-node [params]
       (reagent/as-element
        [ui.dag/node-handles
         [:f> my-node' (js->clj params :keywordize-keys true)]]))"
  [& args]
  (let [[_props children] (separate-props args)]
    (into [:<> [:> Handle {:type "target" :position Position.Left :class "opacity-0"}]]
          (conj children
                [:> Handle {:type "source" :position Position.Right :class "opacity-0"}]))))
